import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ServerHtmlDirective } from 'ish-core/directives/server-html.directive';
import { AccountFacade } from 'ish-core/facades/account.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { AuthAnalyticsService } from 'ish-core/services/auth-analytics/auth-analytics.service';
import { ErrorMessageComponent } from 'ish-shared/components/common/error-message/error-message.component';
import { AuthFieldValidationIconsComponent } from 'ish-shared/components/login/auth-modals/auth-field-validation-icons.component';

/**
 * Presidio — Forgot password as NgbModal overlay on Sign-in.
 */
@Component({
  selector: 'ish-forgot-password-modal',
  templateUrl: './forgot-password-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TranslatePipe,
    ErrorMessageComponent,
    ServerHtmlDirective,
    AuthFieldValidationIconsComponent,
  ],
})
export class ForgotPasswordModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private accountFacade = inject(AccountFacade);
  private authAnalytics = inject(AuthAnalyticsService);

  success$: Observable<boolean>;
  error$: Observable<HttpError>;
  loading$: Observable<boolean>;

  form = new FormGroup({
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
  });

  ngOnInit(): void {
    this.success$ = this.accountFacade.passwordReminderSuccess$;
    this.error$ = this.accountFacade.passwordReminderError$;
    this.loading$ = this.accountFacade.userLoading$;
    this.accountFacade.resetPasswordReminder();
  }

  invalidEmail(): boolean {
    const control = this.form.controls.email;
    return !!(control.invalid && (control.touched || control.dirty));
  }

  showValidEmail(): boolean {
    const control = this.form.controls.email;
    return !!(control.valid && !!control.value.trim() && (control.touched || control.dirty));
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.authAnalytics.trackPasswordResetStart();
    this.accountFacade.requestPasswordReminder({ email: this.form.controls.email.value });
  }

  hide(): void {
    this.activeModal.dismiss();
  }
}
